import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {FormBuilder, FormGroup} from '@angular/forms';

import {ConfigurableOfficeInfo, OfficeInfo, ProblemInstanceService} from 'planner-shared';

@Component({
    selector: 'app-optimization-preferences',
    templateUrl: './optimization-preferences.component.html',
    styleUrls: ['./optimization-preferences.component.scss'],
    standalone: false
})
export class OptimizationPreferencesComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly destroyRef = inject(DestroyRef);

    @Input() public disable: boolean;
    public objectivesForm: FormGroup;
    public criticalPrioritySelected: boolean;
    public highPrioritySelected: boolean;
    public threeOrMoreLowPrioritiesSelected: boolean;
    public rostersPresentInInstance: boolean;

    @Output() settingsChange = new EventEmitter<void>();

    private officeInfo: OfficeInfo;
    private timeWindowWeightDefault = 2;
    private visitHistoryWeightDefault = 2;
    private travelTimeWeightDefault = 2;
    private overqualifiedWeightDefault = 2;
    private rosterPriorityWeightDefault = 2;

    ngOnInit(): void {
        this.objectivesForm = this.formBuilder.group({
            timeWindowWeight: [this.timeWindowWeightDefault],
            visitHistoryWeight: [this.visitHistoryWeightDefault],
            travelTimeWeight: [this.travelTimeWeightDefault],
            overqualifiedWeight: [this.overqualifiedWeightDefault],
            rosterPriorityWeight: [this.rosterPriorityWeightDefault]
        });

        this.objectivesForm.valueChanges
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => this.settingsChange.emit());

        this.problemInstanceService.getProblemInstance()
            .subscribe(result => {
                this.officeInfo = result.problemInstance.officeInfo;
                this.setObjectives(result.problemInstance.officeInfo);
                this.checkPrioritySelection();
            });
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .subscribe(result => {
                this.rostersPresentInInstance = result;
            });
    }

    public objectiveChange(): void {
        this.checkPrioritySelection();
    }

    public reset(officeInfo?: OfficeInfo): void {
        if (officeInfo) {
            this.setObjectives(officeInfo);
        } else {
            this.objectivesForm.controls.timeWindowWeight.setValue(this.timeWindowWeightDefault, { emitEvent: false });
            this.objectivesForm.controls.visitHistoryWeight.setValue(this.visitHistoryWeightDefault, { emitEvent: false });
            this.objectivesForm.controls.travelTimeWeight.setValue(this.travelTimeWeightDefault, { emitEvent: false });
            this.objectivesForm.controls.overqualifiedWeight.setValue(this.overqualifiedWeightDefault, { emitEvent: false });
            this.objectivesForm.controls.rosterPriorityWeight.setValue(this.rosterPriorityWeightDefault, { emitEvent: false });
        }

        this.checkPrioritySelection();
    }

    public updateProblemInstance(): void {
        this.mapConfiguration(this.officeInfo);
    }

    public mapConfiguration<T extends ConfigurableOfficeInfo>(configuration: T): void {
        configuration.timeWindowWeight = this.objectivesForm.value.timeWindowWeight;
        configuration.travelTimeWeight = this.objectivesForm.value.travelTimeWeight;
        configuration.visitHistoryWeight = this.objectivesForm.value.visitHistoryWeight;
        configuration.overqualifiedWeight = this.objectivesForm.value.overqualifiedWeight;
        configuration.rosterPriorityWeight = this.objectivesForm.value.rosterPriorityWeight;
    }

    private setObjectives(officeInfo: OfficeInfo): void {
        if (officeInfo.timeWindowWeight == null) {
            officeInfo.timeWindowWeight = this.timeWindowWeightDefault;
        } else {
            this.objectivesForm.controls.timeWindowWeight.setValue(officeInfo.timeWindowWeight, { emitEvent: false });
        }

        if (officeInfo.travelTimeWeight == null) {
            officeInfo.travelTimeWeight = this.travelTimeWeightDefault;
        } else {
            this.objectivesForm.controls.travelTimeWeight.setValue(officeInfo.travelTimeWeight, { emitEvent: false });
        }

        if (officeInfo.visitHistoryWeight == null) {
            officeInfo.visitHistoryWeight = this.visitHistoryWeightDefault;
        } else {
            this.objectivesForm.controls.visitHistoryWeight.setValue(officeInfo.visitHistoryWeight, { emitEvent: false });
        }

        if (officeInfo.overqualifiedWeight == null) {
            officeInfo.overqualifiedWeight = this.overqualifiedWeightDefault;
        } else {
            this.objectivesForm.controls.overqualifiedWeight.setValue(officeInfo.overqualifiedWeight, { emitEvent: false });
        }

        if (officeInfo.rosterPriorityWeight == null) {
            officeInfo.rosterPriorityWeight = this.rosterPriorityWeightDefault;
        } else {
            this.objectivesForm.controls.rosterPriorityWeight.setValue(officeInfo.rosterPriorityWeight, { emitEvent: false });
        }
    }

    private checkPrioritySelection(): void {
        const values = Object.values(this.objectivesForm.value);
        this.criticalPrioritySelected = values.some(value => value === 6);
        this.highPrioritySelected = values.some(value => value === 3);
        this.threeOrMoreLowPrioritiesSelected = values.filter(value => value === 1).length >= 3;
    }
}
